// src/pages/auth-callback/index.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import { PLAN_SLUGS, normalizePlanSlug } from '../../config/plans';

export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const redirect = params.get('redirect') || '/main-dashboard';

  const ranRef = useRef(false);
  const [localError, setLocalError] = useState('');

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const run = async () => {
      try {
        // ✅ 1) Toujours lire la session depuis Supabase (pas depuis le context)
        const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
        if (sessionError) {
          console.error('[AuthCallback] getSession error:', sessionError);
        }

        const sessionUser = sessionData?.session?.user;

        // Si pas de session, on retourne login
        if (!sessionUser?.id) {
          navigate('/login', { replace: true, state: { from: redirect } });
          return;
        }

        // ✅ 2) Récupère ce qu’on a stocké avant l’OAuth (Register step 1)
        let pending = null;
        try {
          pending = JSON.parse(sessionStorage.getItem('pending_profile') || 'null');
        } catch {
          pending = null;
        }

        // ✅ 3) Tier choisi (fallback)
        const intendedTier =
          normalizePlanSlug(
            pending?.intended_tier ||
            sessionStorage.getItem('intended_tier') ||
            PLAN_SLUGS.TRIAL
          );

        // ✅ 4) Valeurs de base (fallback depuis metadata supabase si dispo)
        const meta = sessionUser?.user_metadata || {};
        const fullName =
          pending?.full_name ||
          meta?.full_name ||
          meta?.name ||
          `${pending?.first_name || ''} ${pending?.last_name || ''}`.trim();

        const profilePayload = {
          id: sessionUser.id,
          full_name: fullName || null,
          first_name: pending?.first_name || meta?.first_name || null,
          last_name: pending?.last_name || meta?.last_name || null,
          subscription_tier: intendedTier,
          subscribe_newsletter:
            typeof pending?.subscribe_newsletter === 'boolean'
              ? pending.subscribe_newsletter
              : true
        };

        // ✅ 5) Upsert user_profiles
        const { error: upsertError } = await supabase
          .from('user_profiles')
          .upsert(profilePayload, { onConflict: 'id' });

        if (upsertError) {
          console.error('[AuthCallback] Profile upsert error:', upsertError);
          setLocalError(
            "On a eu un petit hic en finalisant votre profil. Vous pouvez continuer quand même."
          );
        }

        // ✅ 6) Nettoyage
        sessionStorage.removeItem('pending_profile');
        sessionStorage.removeItem('intended_tier');

        // ✅ 7) Redirect final
        navigate(redirect, { replace: true });
      } catch (err) {
        console.error('[AuthCallback] Unexpected error:', err);
        setLocalError('Erreur lors de la connexion. Veuillez réessayer.');
        navigate('/login', { replace: true, state: { from: redirect } });
      }
    };

    run();
  }, [navigate, redirect]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center max-w-md px-6">
        <Icon name="Loader2" size={42} className="animate-spin mx-auto mb-3 text-primary" />
        <p className="text-muted-foreground">Connexion en cours…</p>

        {localError && (
          <div className="mt-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            {localError}
          </div>
        )}
      </div>
    </div>
  );
}
